import React, { Dispatch, useCallback, useMemo } from 'react';
import classes from './ActiveFilterGroup.module.scss';
import { FilterItemGroup } from '../FilterItemGroup';
import classNames from 'classnames';
import { CoreCategoryFieldsFragment, SocialProfileFieldsFragment } from 'models/graphql';
import { PostScheduleFilterAction, PostScheduleFilterType } from 'models/post';
import { TimeslotFilterAction, TimeslotFilterType } from 'models/calendar';
import {
  FindImagesFilterAction,
  FindImagesFilterType,
  MediaLibraryFilterAction,
  MediaLibraryFilterType
} from 'models/media';
import { SocialProfile } from 'components/socialProfiles/SocialProfile';
import { profilesHelpers } from 'modules/Settings/Profiles/Profiles.helpers';
import { SquareButton } from 'components/actions/SquareButton';
import { SquareButtonIconsEnum } from 'constants/square-button-icons';
import { Void } from '../../../models/basic-objects';
import { Debugger } from '../../containers/Debugger';

export type ActiveFilterTypes =
  | PostScheduleFilterType
  | TimeslotFilterType
  | MediaLibraryFilterType
  | FindImagesFilterType;

// @TODO: Why is not being used?
export type ActiveFilterDispatchActionType =
  | PostScheduleFilterAction
  | MediaLibraryFilterAction
  | FindImagesFilterAction
  | TimeslotFilterAction;

export enum PostTimingFilterFriendlyNames {
  SET_DATES = 'Fixed Dates & Times',
  RECURRING = 'Recurring',
  QUEUED = 'Posting Plan Queue'
}

export enum PostEvergreenFilterFriendlyNames {
  EVERGREEN_ONLY = 'Evergreen Only'
}

export enum PostFeedTypeFilterFriendlyNames {
  FEED = 'FEED',
  STORY = 'STORY',
  REEL = 'REEL'
}

export enum PostSourceFilterFriendlyNames {
  POST_EDITOR = 'Post Editor',
  CSV_UPLOAD = 'CSV Upload',
  SOCIAL_IMPORT = 'RSS/Social Import',
  AUTO_RSS = 'RSS Auto-Importer'
}

export enum ContentFilterFriendlyNames {
  IMAGE = 'Image',
  IMAGE_LINK = 'Image + Link',
  LINK = 'Link',
  TEXT = 'Text',
  VIDEO = 'Video',
  VIDEO_LINK = 'Video + Link'
}

export enum MediaTypeFilterFriendlyName {
  GIFS = 'GIFs',
  VIDEOS = 'Videos',
  IMAGES = 'Images',
  PHOTO = 'Photos',
  ILLUSTRATION = 'Illustrations',
  VECTOR = 'Vectors'
}

export enum MediaProportionFilterFriendlyName {
  SQUARE = 'Square',
  TALL = 'Tall',
  WIDE = 'Wide',
  HORIZONTAL = 'Wide',
  VERTICAL = 'Tall'
}

export enum MediaOriginFilterFriendlyName {
  CANVA = 'Canva',
  WEB = 'Web',
  UPLOAD = 'Upload'
}

export enum IgnoredFilterKeysEnum {
  tempProfiles = 'tempProfiles',
  tempCategories = 'tempCategories',
  status = 'status',
  fetchPosts = 'fetchPosts'
}

const friendlyNamesMap = {
  ...PostTimingFilterFriendlyNames,
  ...PostEvergreenFilterFriendlyNames,
  ...PostFeedTypeFilterFriendlyNames,
  ...PostSourceFilterFriendlyNames,
  ...ContentFilterFriendlyNames,
  ...MediaTypeFilterFriendlyName,
  ...MediaProportionFilterFriendlyName,
  ...MediaOriginFilterFriendlyName
};

// Change key to a friendly user facing name
export const mapActionFilterKeyToFriendlyName = (key: string): string | undefined => {
  return friendlyNamesMap[key as keyof typeof friendlyNamesMap] || key;
};

// Check if key is in IgnoredFilterKeysEnum
export const isIgnoredFilterKey = (key: string): boolean => {
  return Object.values(IgnoredFilterKeysEnum).includes(key as IgnoredFilterKeysEnum);
};

export type ActiveFilterTypedProps =
  | {
      dispatchFilters: Dispatch<PostScheduleFilterAction>;
    }
  | {
      dispatchFilters: Dispatch<TimeslotFilterAction>;
    }
  | {
      dispatchFilters: Dispatch<FindImagesFilterAction>;
    }
  | {
      dispatchFilters: Dispatch<MediaLibraryFilterAction>;
    };

export type ActiveFilterGroupViewProps = {
  filters: ActiveFilterTypes;
  category?: CoreCategoryFieldsFragment;
  profile?: SocialProfileFieldsFragment;
  loading?: boolean;
  isGenericFiltersOnly?: boolean;
} & ActiveFilterTypedProps;

export const ActiveFilterGroupView = ({
  filters,
  profile,
  category,
  dispatchFilters,
  loading,
  isGenericFiltersOnly
}: ActiveFilterGroupViewProps): JSX.Element => {
  const shouldShowActiveFilters = !!Object.entries(filters).filter(([key, value]) => !isIgnoredFilterKey(key) && value)
    .length;

  const filterClasses = classNames('grid-x', 'align-left', 'align-middle');
  const clearFilterItem = useCallback(
    (filterName: keyof ActiveFilterTypes) => {
      if (!isGenericFiltersOnly) {
        const payload =
          filterName === 'showOutOfSeason'
            ? { payload: false }
            : filterName !== 'resetAll'
            ? { payload: { data: '', fetchPosts: true } }
            : {};
        dispatchFilters({ type: filterName, ...payload });
      } else {
        if (filterName === 'resetAll') {
          dispatchFilters({ type: 'resetAll' });
        } else {
          dispatchFilters({ type: filterName });
        }
      }
    },
    [dispatchFilters, isGenericFiltersOnly]
  );

  const activeFilters = useMemo(
    () =>
      Object.entries(filters)
        .filter(([key]) => !isIgnoredFilterKey(key))
        .map(([key, value]) => {
          const actionHandler: Void = () => clearFilterItem(key as keyof ActiveFilterTypes);

          let filterItemContent: JSX.Element | string | undefined;
          switch (key) {
            case 'category':
            case 'categories':
              filterItemContent = category ? ActiveCategoryFilterItem(category) : undefined;
              break;
            case 'profile':
            case 'profiles':
              filterItemContent = profile ? ActiveProfileFilterItem(profile) : undefined;
              break;
            case 'showOutOfSeason':
              filterItemContent = value ? <div className={'cell auto'}>Show out-of-season</div> : undefined;
              break;
            default:
              filterItemContent = value ? (
                <div className={classNames('cell auto', classes.FilterGenericText)}>
                  {mapActionFilterKeyToFriendlyName(String(value))}
                </div>
              ) : undefined;
          }

          return filterItemContent ? (
            <div key={key} className={classes.ActiveFilterItem}>
              {category && ['category', 'categories'].includes(key) && (
                <div className={'relative'}>
                  <Debugger tooltipContent={'Category ID: ' + category.id} modalContent={category} />
                </div>
              )}
              <div className={filterClasses}>
                {filterItemContent}
                <div className={classNames('cell shrink', classes.CloseButtonContainer)}>
                  <SquareButton
                    icon={SquareButtonIconsEnum.closeSmall}
                    type={'transparent'}
                    disabled={loading}
                    action={actionHandler}
                  />
                </div>
              </div>
            </div>
          ) : undefined;
        }),
    [category, clearFilterItem, filterClasses, filters, loading, profile]
  );

  return shouldShowActiveFilters ? (
    <div className={classes.ActiveFilterGroup}>
      <FilterItemGroup
        title={'Current Filters'}
        isFiltered={true}
        resetHandler={() => dispatchFilters({ type: 'resetAll' })}>
        {activeFilters}
      </FilterItemGroup>
    </div>
  ) : (
    <></>
  );
};

export const ActiveCategoryFilterItem = (category: CoreCategoryFieldsFragment): JSX.Element => {
  const categoryClasses = classNames(classes.ColorBar, classes[`ColorBar${category.color}`]);

  return (
    <>
      <div className={categoryClasses} />
      <div className={`cell auto ${classes.Label}`}>{category.name}</div>
    </>
  );
};

export const ActiveProfileFilterItem = (profile: SocialProfileFieldsFragment): JSX.Element => {
  const provider = profilesHelpers.mapOAuthFromTypename(profile.__typename);
  const network = profilesHelpers.mapNetworkFromTypename(profile.__typename);

  return (
    <div className={'cell auto'}>
      <SocialProfile
        size={'small'}
        label={true}
        disconnected={!profile.connected}
        socialProfile={{
          id: profile.id,
          name: profile.displayName,
          provider: provider,
          network: network,
          genericProfile: profile.__typename === 'GenericSocialProfile' ? profile.genericProfile : undefined
        }}
        image={profile.photoUrl}
      />
    </div>
  );
};
